import * as React from "react";
import { RefreshCwIcon } from "lucide-react";

import { useTranslations } from "@/i18n";
import type {
  DataTableAction,
  DataTableToolbarActionInput,
  DataTableToolbarConfig,
  TableFilterConfig,
  TableFilterValues,
} from "@ibada/ui/components/data-table";

/**
 * The declarative toolbar renders only structured actions. Legacy `{ id, node }` actions have no
 * slot in it — drop them rather than crash, and make the omission visible in development.
 */
function toStructuredActions(actions: DataTableToolbarActionInput[] | undefined): DataTableAction[] {
  if (!actions?.length) return [];

  const structured = actions.filter((action): action is DataTableAction => !("node" in action));

  return structured;
}

export type DataTableToolbarSortItem = { id: string; label: string };

export type UseDataTableToolbarConfigOptions = {
  /** Resolved filter config from `useTableQuery`. Its `search` slot drives the toolbar search box. */
  filterConfig: TableFilterConfig;
  appliedFilters: TableFilterValues;
  onApplyFilters: (values: TableFilterValues) => void;
  onResetFilters: () => void;
  /** Toolbar actions other than refresh. A single `emphasis: "primary"` action becomes `primaryAction`. */
  actions?: DataTableToolbarActionInput[];
  /** When provided, a priority "Refresh" action is appended to the secondary actions. */
  onRefresh?: () => Promise<unknown> | void;
  isRefreshing?: boolean;
  /** Server-sort menu. Omit for tables that don't sort server-side (preserves the old toolbar's behavior). */
  sorting?: {
    items: DataTableToolbarSortItem[];
  };
  /** Set false for tables that intentionally hide the column-visibility menu. */
  showDisplayOptions?: boolean;
};

/**
 * Adapter from the legacy `<DataTableFilters>` render-prop toolbar to the declarative
 * `DataTableToolbarConfig`. Every table used to hand-roll the same wiring (search + filters +
 * refresh + actions); this centralises it so a migrated table is one `toolbar={toolbarConfig}` prop.
 *
 * Labels come from the shared `common.*` namespace, so callers need no new translation keys.
 */
export function useDataTableToolbarConfig<TData>({
  filterConfig,
  appliedFilters,
  onApplyFilters,
  onResetFilters,
  actions,
  onRefresh,
  isRefreshing = false,
  sorting,
  showDisplayOptions = true,
}: UseDataTableToolbarConfigOptions): DataTableToolbarConfig<TData> {
  const tCommon = useTranslations("common");

  const structuredActions = React.useMemo(() => toStructuredActions(actions), [actions]);

  // Some toolbars declare more than one first-class action (finance transactions offers both
  // "New income" and "New expense"). Keep them all inline — taking only the first silently drops
  // the others, which is how the expense button went missing.
  const primaryAction = React.useMemo(() => {
    const primaries = structuredActions.filter((action) => action.emphasis === "primary");
    if (primaries.length === 0) return undefined;
    return primaries.length === 1 ? primaries[0] : primaries;
  }, [structuredActions]);

  const secondaryActions = React.useMemo(() => {
    const rest = structuredActions.filter((action) => action.emphasis !== "primary");

    if (!onRefresh) return rest;

    return [
      ...rest,
      {
        id: "refresh",
        label: tCommon("refresh"),
        icon: <RefreshCwIcon className='size-4' />,
        emphasis: "priority",
        loading: isRefreshing,
        onSelect: () => {
          void onRefresh();
        },
      } satisfies DataTableAction,
    ];
  }, [isRefreshing, onRefresh, structuredActions, tCommon]);

  return React.useMemo<DataTableToolbarConfig<TData>>(
    () => ({
      search: filterConfig.search
        ? {
            value: filterConfig.search.value,
            onValueChange: filterConfig.search.onChange,
            placeholder: filterConfig.search.placeholder ?? tCommon("dataTable.searchPlaceholder"),
            label: filterConfig.search.placeholder ?? tCommon("dataTable.searchPlaceholder"),
            debounceMs: filterConfig.search.debounceMs,
          }
        : undefined,
      sorting: sorting
        ? {
            label: tCommon("sort"),
            ascendingLabel: tCommon("ascending"),
            descendingLabel: tCommon("descending"),
            items: sorting.items,
          }
        : undefined,
      filters: {
        config: filterConfig,
        appliedValues: appliedFilters,
        onApply: onApplyFilters,
        onReset: onResetFilters,
      },
      display: showDisplayOptions
        ? {
            label: tCommon("dataTable.viewOptions.columns"),
            activeColumnsLabel: tCommon("dataTable.viewOptions.activeColumns"),
            availableColumnsLabel: tCommon("dataTable.viewOptions.availableColumns"),
            emptyColumnsLabel: tCommon("dataTable.viewOptions.emptyColumns"),
            resetLabel: tCommon("dataTable.viewOptions.resetColumns"),
          }
        : undefined,
      primaryAction,
      secondaryActions,
    }),
    [
      appliedFilters,
      filterConfig,
      onApplyFilters,
      onResetFilters,
      primaryAction,
      secondaryActions,
      showDisplayOptions,
      sorting,
      tCommon,
    ],
  );
}
